import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useClienteAuth } from '../../context/ClienteAuthContext';
import toast from 'react-hot-toast';

const ESTADOS_BR = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS',
  'MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

const STATUS_CORES = {
  pendente:   { bg: '#fef3c7', color: '#92400e', label: 'Pendente' },
  aceito:     { bg: '#dbeafe', color: '#1e40af', label: 'Aceito' },
  em_rota:    { bg: '#ede9fe', color: '#5b21b6', label: 'Em rota' },
  entregue:   { bg: '#dcfce7', color: '#166534', label: 'Entregue' },
  retirado:   { bg: '#dcfce7', color: '#166534', label: 'Retirado' },
  cancelado:  { bg: '#fee2e2', color: '#991b1b', label: 'Cancelado' },
};

const INPUT_STYLE = {
  padding: '12px 14px',
  borderRadius: 12,
  border: '1.5px solid #e8e4de',
  fontSize: '16px',
  fontFamily: "'Sora', sans-serif",
  background: '#fff',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  WebkitAppearance: 'none',
};

function StatusBadge({ status }) {
  const s = STATUS_CORES[status] || { bg: '#f3f4f6', color: '#374151', label: status };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 20,
      background: s.bg, color: s.color, fontSize: '.72rem', fontWeight: 700,
    }}>
      {s.label}
    </span>
  );
}

function formatBRL(v) {
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatData(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatCep(v) {
  const d = v.replace(/\D/g, '').slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

// Modal de endereço
function ModalEndereco({ inicial, onSalvar, onFechar, loading }) {
  const [form, setForm] = useState(inicial || {
    apelido: 'Casa', cep: '', endereco: '', numero: '', complemento: '',
    bairro: '', cidade: '', estado: '',
  });
  const [buscandoCep, setBuscandoCep] = useState(false);
  const F = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const buscarCep = async (valor) => {
    const cep = (valor || '').replace(/\D/g, '');
    if (cep.length !== 8) return;
    setBuscandoCep(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await resp.json();
      if (data.erro) { toast.error('CEP não encontrado'); return; }
      setForm(p => ({
        ...p,
        endereco: data.logradouro || p.endereco,
        bairro: data.bairro || p.bairro,
        cidade: data.localidade || p.cidade,
        estado: data.uf || p.estado,
      }));
    } catch {
      toast.error('Não foi possível buscar o CEP');
    } finally {
      setBuscandoCep(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.endereco.trim()) { toast.error('Endereço é obrigatório'); return; }
    onSalvar(form);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)',
      zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }} onClick={e => e.target === e.currentTarget && onFechar()}>
      <div style={{
        background: '#fff', borderRadius: '20px 20px 0 0', width: '100%',
        maxWidth: 520, maxHeight: '90dvh', overflowY: 'auto',
        padding: '24px 20px 32px',
        paddingBottom: 'max(32px, env(safe-area-inset-bottom))',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontWeight: 800, fontSize: '1rem', margin: 0 }}>
            {inicial?.id ? 'Editar endereço' : 'Novo endereço'}
          </h3>
          <button onClick={onFechar} style={{
            background: '#f3f4f6', border: 'none', borderRadius: 8,
            width: 32, height: 32, cursor: 'pointer', fontSize: '1rem',
          }}>✕</button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            { label: 'Apelido (ex: Casa, Trabalho)', key: 'apelido' },
          ].map(({ label, key }) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: '.78rem', fontWeight: 700, color: '#444' }}>{label}</label>
              <input value={form[key] || ''} onChange={e => F(key, e.target.value)}
                style={INPUT_STYLE}
                onFocus={e => e.target.style.borderColor = '#FF6B35'}
                onBlur={e => e.target.style.borderColor = '#e8e4de'}
              />
            </div>
          ))}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '.78rem', fontWeight: 700, color: '#444' }}>CEP</label>
            <div style={{ position: 'relative' }}>
              <input
                value={form.cep || ''}
                onChange={e => F('cep', formatCep(e.target.value))}
                onBlur={e => { e.target.style.borderColor = '#e8e4de'; buscarCep(e.target.value); }}
                onFocus={e => e.target.style.borderColor = '#FF6B35'}
                placeholder="00000-000"
                inputMode="numeric"
                maxLength={9}
                style={{ ...INPUT_STYLE, paddingRight: 36 }}
              />
              {buscandoCep && (
                <span style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  width: 16, height: 16, border: '2px solid #eee', borderTopColor: '#FF6B35',
                  borderRadius: '50%', animation: 'spin .7s linear infinite',
                }} />
              )}
            </div>
          </div>

          {[
            { label: 'Endereço / Logradouro *', key: 'endereco' },
          ].map(({ label, key }) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: '.78rem', fontWeight: 700, color: '#444' }}>{label}</label>
              <input value={form[key] || ''} onChange={e => F(key, e.target.value)}
                style={INPUT_STYLE}
                onFocus={e => e.target.style.borderColor = '#FF6B35'}
                onBlur={e => e.target.style.borderColor = '#e8e4de'}
              />
            </div>
          ))}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { label: 'Número', key: 'numero' },
              { label: 'Complemento', key: 'complemento' },
            ].map(({ label, key }) => (
              <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: '.78rem', fontWeight: 700, color: '#444' }}>{label}</label>
                <input value={form[key] || ''} onChange={e => F(key, e.target.value)}
                  style={INPUT_STYLE}
                  onFocus={e => e.target.style.borderColor = '#FF6B35'}
                  onBlur={e => e.target.style.borderColor = '#e8e4de'}
                />
              </div>
            ))}
          </div>
          {[
            { label: 'Bairro', key: 'bairro' },
            { label: 'Cidade', key: 'cidade' },
          ].map(({ label, key }) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: '.78rem', fontWeight: 700, color: '#444' }}>{label}</label>
              <input value={form[key] || ''} onChange={e => F(key, e.target.value)}
                style={INPUT_STYLE}
                onFocus={e => e.target.style.borderColor = '#FF6B35'}
                onBlur={e => e.target.style.borderColor = '#e8e4de'}
              />
            </div>
          ))}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '.78rem', fontWeight: 700, color: '#444' }}>Estado</label>
            <select value={form.estado || ''} onChange={e => F('estado', e.target.value)}
              style={{ ...INPUT_STYLE, background: '#fff' }}>
              <option value="">Selecione</option>
              {ESTADOS_BR.map(uf => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </div>
          <button type="submit" disabled={loading} style={{
            marginTop: 8, background: loading ? '#ccc' : '#FF6B35',
            color: '#fff', border: 'none', borderRadius: 14,
            padding: '14px', fontWeight: 800, fontSize: '1rem',
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>
            {loading ? 'Salvando...' : 'Salvar endereço'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Aba Pedidos ─────────────────────────────────────────────────
function AbaPedidos({ getPedidos }) {
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 10;

  const carregar = useCallback(async (pg) => {
    setLoading(true);
    try {
      const { data } = await getPedidos({ page: pg, limit: LIMIT });
      setPedidos(data.dados);
      setTotal(data.total);
    } catch {
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  }, [getPedidos]);

  useEffect(() => { carregar(page); }, [page, carregar]);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 40, color: '#aaa', fontSize: '.9rem' }}>
      Carregando pedidos...
    </div>
  );

  if (!pedidos.length) return (
    <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🛒</div>
      <p style={{ fontWeight: 700, marginBottom: 6 }}>Nenhum pedido ainda</p>
      <p style={{ fontSize: '.85rem' }}>Seus pedidos aparecerão aqui.</p>
    </div>
  );

  return (
    <div>
      {pedidos.map(p => (
        <div key={p.id} style={{
          background: '#fff', borderRadius: 14, padding: '16px',
          marginBottom: 10, border: '1px solid #eee',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: '.9rem', marginBottom: 2 }}>{p.empresa}</div>
              <div style={{ fontSize: '.75rem', color: '#999' }}>{formatData(p.data_hora)}</div>
            </div>
            <StatusBadge status={p.status} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 800, fontSize: '1rem', color: '#FF6B35' }}>
              {formatBRL(p.valor_total)}
            </span>
            <button onClick={() => navigate(`/acompanhar/${p.id}`)} style={{
              background: '#1A1A2E', color: '#fff', border: 'none', borderRadius: 10,
              padding: '8px 14px', fontWeight: 700, fontSize: '.8rem',
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
              Acompanhar
            </button>
          </div>
        </div>
      ))}
      {total > LIMIT && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{
            padding: '8px 16px', borderRadius: 10, border: '1.5px solid #e8e4de',
            background: page === 1 ? '#f9f9f9' : '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit', fontWeight: 700, fontSize: '.85rem',
          }}>← Anterior</button>
          <span style={{ padding: '8px 12px', fontSize: '.85rem', color: '#888' }}>
            {page} / {Math.ceil(total / LIMIT)}
          </span>
          <button disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)} style={{
            padding: '8px 16px', borderRadius: 10, border: '1.5px solid #e8e4de',
            background: page * LIMIT >= total ? '#f9f9f9' : '#fff',
            cursor: page * LIMIT >= total ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit', fontWeight: 700, fontSize: '.85rem',
          }}>Próxima →</button>
        </div>
      )}
    </div>
  );
}

// ── Aba Endereços ────────────────────────────────────────────────
function AbaEnderecos({ getEnderecos, postEndereco, putEndereco, deleteEndereco, patchPadrao }) {
  const [enderecos, setEnderecos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [modal, setModal] = useState(null); // null | {} | {id, ...}

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getEnderecos();
      setEnderecos(data);
    } catch {
      toast.error('Erro ao carregar endereços');
    } finally {
      setLoading(false);
    }
  }, [getEnderecos]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleSalvar = async (form) => {
    setSalvando(true);
    try {
      if (form.id) {
        await putEndereco(form.id, form);
        toast.success('Endereço atualizado!');
      } else {
        await postEndereco(form);
        toast.success('Endereço adicionado!');
      }
      setModal(null);
      await carregar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao salvar endereço');
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async (id) => {
    if (!window.confirm('Excluir este endereço?')) return;
    try {
      await deleteEndereco(id);
      toast.success('Endereço excluído');
      await carregar();
    } catch {
      toast.error('Erro ao excluir endereço');
    }
  };

  const handlePadrao = async (id) => {
    try {
      await patchPadrao(id);
      await carregar();
      toast.success('Endereço padrão atualizado');
    } catch {
      toast.error('Erro ao definir padrão');
    }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 40, color: '#aaa', fontSize: '.9rem' }}>
      Carregando endereços...
    </div>
  );

  return (
    <div>
      <button onClick={() => setModal({})} style={{
        width: '100%', background: '#FF6B35', color: '#fff', border: 'none',
        borderRadius: 14, padding: '13px', fontWeight: 800, fontSize: '.9rem',
        cursor: 'pointer', fontFamily: 'inherit', marginBottom: 14,
      }}>
        + Novo Endereço
      </button>

      {!enderecos.length ? (
        <div style={{ textAlign: 'center', padding: '24px 0', color: '#aaa' }}>
          <div style={{ fontSize: '2.2rem', marginBottom: 10 }}>📍</div>
          <p style={{ fontWeight: 700, marginBottom: 4 }}>Nenhum endereço salvo</p>
          <p style={{ fontSize: '.83rem' }}>Adicione um endereço para facilitar seus pedidos.</p>
        </div>
      ) : enderecos.map(e => (
        <div key={e.id} style={{
          background: '#fff', borderRadius: 14, padding: '16px',
          marginBottom: 10, border: `1.5px solid ${e.padrao ? '#FF6B35' : '#eee'}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontWeight: 800, fontSize: '.9rem' }}>{e.apelido}</span>
            {e.padrao && (
              <span style={{
                background: '#fff1ec', color: '#FF6B35', fontSize: '.7rem',
                fontWeight: 800, padding: '3px 10px', borderRadius: 20,
              }}>Padrão</span>
            )}
          </div>
          <p style={{ fontSize: '.83rem', color: '#555', margin: '0 0 12px', lineHeight: 1.5 }}>
            {[e.endereco, e.numero, e.complemento].filter(Boolean).join(', ')}
            {e.bairro ? ` — ${e.bairro}` : ''}
            {e.cidade ? `, ${e.cidade}` : ''}
            {e.estado ? `/${e.estado}` : ''}
            {e.cep ? ` — CEP ${e.cep}` : ''}
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!e.padrao && (
              <button onClick={() => handlePadrao(e.id)} style={{
                padding: '7px 12px', borderRadius: 9, border: '1.5px solid #e8e4de',
                background: '#fafaf8', cursor: 'pointer', fontFamily: 'inherit',
                fontSize: '.77rem', fontWeight: 700, color: '#444',
              }}>Definir como padrão</button>
            )}
            <button onClick={() => setModal(e)} style={{
              padding: '7px 12px', borderRadius: 9, border: '1.5px solid #e8e4de',
              background: '#fafaf8', cursor: 'pointer', fontFamily: 'inherit',
              fontSize: '.77rem', fontWeight: 700, color: '#444',
            }}>Editar</button>
            <button onClick={() => handleExcluir(e.id)} style={{
              padding: '7px 12px', borderRadius: 9, border: '1.5px solid #fee2e2',
              background: '#fff5f5', cursor: 'pointer', fontFamily: 'inherit',
              fontSize: '.77rem', fontWeight: 700, color: '#ef4444',
            }}>Excluir</button>
          </div>
        </div>
      ))}

      {modal !== null && (
        <ModalEndereco
          inicial={modal?.id ? modal : null}
          onSalvar={handleSalvar}
          onFechar={() => setModal(null)}
          loading={salvando}
        />
      )}
    </div>
  );
}

// ── Aba Perfil ──────────────────────────────────────────────────
function AbaPerfil({ getPerfil, putPerfil, logoutCliente }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ nome: '', telefone: '', cpf: '', email: '' });
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const F = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    getPerfil()
      .then(({ data }) => setForm({
        nome: data.nome || '',
        telefone: data.telefone || '',
        cpf: data.cpf || '',
        email: data.email || '',
      }))
      .catch(() => toast.error('Erro ao carregar perfil'))
      .finally(() => setLoading(false));
  }, [getPerfil]);

  const handleSalvar = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) { toast.error('Nome é obrigatório'); return; }
    setSalvando(true);
    try {
      await putPerfil({ nome: form.nome, telefone: form.telefone, cpf: form.cpf });
      toast.success('Perfil atualizado!');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao atualizar perfil');
    } finally {
      setSalvando(false);
    }
  };

  const handleLogout = () => {
    logoutCliente();
    navigate('/cliente/login', { replace: true });
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 40, color: '#aaa', fontSize: '.9rem' }}>
      Carregando perfil...
    </div>
  );

  return (
    <form onSubmit={handleSalvar} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {[
        { label: 'Nome completo *', key: 'nome', type: 'text', editavel: true },
        { label: 'E-mail', key: 'email', type: 'email', editavel: false },
        { label: 'Telefone / WhatsApp', key: 'telefone', type: 'tel', editavel: true },
        { label: 'CPF', key: 'cpf', type: 'text', editavel: true },
      ].map(({ label, key, type, editavel }) => (
        <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: '.8rem', fontWeight: 700, color: '#444' }}>{label}</label>
          <input
            type={type} value={form[key]} disabled={!editavel}
            onChange={e => editavel && F(key, e.target.value)}
            style={{
              ...INPUT_STYLE,
              background: editavel ? '#fff' : '#f5f5f5',
              color: editavel ? '#222' : '#888',
              cursor: editavel ? 'text' : 'not-allowed',
            }}
            onFocus={e => editavel && (e.target.style.borderColor = '#FF6B35')}
            onBlur={e => editavel && (e.target.style.borderColor = '#e8e4de')}
          />
          {!editavel && (
            <span style={{ fontSize: '.72rem', color: '#bbb' }}>O e-mail não pode ser alterado.</span>
          )}
        </div>
      ))}
      <button type="submit" disabled={salvando} style={{
        background: salvando ? '#ccc' : '#FF6B35', color: '#fff', border: 'none',
        borderRadius: 14, padding: '13px', fontWeight: 800, fontSize: '.95rem',
        cursor: salvando ? 'not-allowed' : 'pointer', fontFamily: 'inherit', marginTop: 4,
      }}>
        {salvando ? 'Salvando...' : 'Salvar alterações'}
      </button>
      <button type="button" onClick={handleLogout} style={{
        background: 'none', border: '1.5px solid #e8e4de', borderRadius: 14,
        padding: '13px', fontWeight: 700, fontSize: '.9rem', cursor: 'pointer',
        fontFamily: 'inherit', color: '#666',
      }}>
        Sair da conta
      </button>
    </form>
  );
}

// ── Componente principal ─────────────────────────────────────────
export default function MinhaConta() {
  const navigate = useNavigate();
  const {
    cliente, loadingCliente, logoutCliente,
    getPerfil, putPerfil, getEnderecos, postEndereco,
    putEndereco, deleteEndereco, patchPadrao, getPedidos,
  } = useClienteAuth();

  const [aba, setAba] = useState('pedidos'); // 'pedidos' | 'enderecos' | 'perfil'

  useEffect(() => {
    if (!loadingCliente && !cliente) {
      navigate('/cliente/login', { replace: true });
    }
  }, [cliente, loadingCliente, navigate]);

  if (loadingCliente) return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Sora', sans-serif" }}>
      <div style={{ width: 36, height: 36, border: '3px solid #eee', borderTopColor: '#FF6B35',
        borderRadius: '50%', animation: 'spin .7s linear infinite' }} />
    </div>
  );

  if (!cliente) return null;

  const abas = [
    { key: 'pedidos',   label: 'Meus Pedidos' },
    { key: 'enderecos', label: 'Endereços' },
    { key: 'perfil',    label: 'Perfil' },
  ];

  const tabStyle = (ativo) => ({
    flex: 1, padding: '12px 8px', border: 'none',
    borderBottom: `3px solid ${ativo ? '#FF6B35' : 'transparent'}`,
    background: 'none', fontFamily: "'Sora', sans-serif",
    fontWeight: ativo ? 800 : 600, fontSize: '.82rem',
    color: ativo ? '#FF6B35' : '#888', cursor: 'pointer',
    transition: 'all .15s', whiteSpace: 'nowrap',
  });

  return (
    <div style={{ minHeight: '100dvh', background: '#F8F7F4', fontFamily: "'Sora', sans-serif" }}>
      {/* Header */}
      <header style={{
        background: '#1A1A2E',
        padding: '16px 20px',
        paddingTop: 'max(16px, env(safe-area-inset-top))',
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <button onClick={() => navigate(-1)} style={{
              background: 'rgba(255,255,255,.1)', border: 'none', color: '#fff',
              borderRadius: 10, width: 38, height: 38, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem',
              flexShrink: 0,
            }}>←</button>
            <div>
              <h1 style={{ color: '#fff', fontSize: '1rem', fontWeight: 800, margin: 0 }}>
                Minha Conta
              </h1>
              <p style={{ color: 'rgba(255,255,255,.6)', fontSize: '.78rem', margin: 0 }}>
                {cliente.nome}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Abas */}
      <div style={{ background: '#fff', borderBottom: '1px solid #eee', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: 600, margin: '0 auto', display: 'flex' }}>
          {abas.map(a => (
            <button key={a.key} style={tabStyle(aba === a.key)} onClick={() => setAba(a.key)}>
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* Conteúdo */}
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '16px 16px 40px' }}>
        {aba === 'pedidos' && <AbaPedidos getPedidos={getPedidos} />}
        {aba === 'enderecos' && (
          <AbaEnderecos
            getEnderecos={getEnderecos}
            postEndereco={postEndereco}
            putEndereco={putEndereco}
            deleteEndereco={deleteEndereco}
            patchPadrao={patchPadrao}
          />
        )}
        {aba === 'perfil' && (
          <AbaPerfil
            getPerfil={getPerfil}
            putPerfil={putPerfil}
            logoutCliente={logoutCliente}
          />
        )}
      </div>
    </div>
  );
}
